import _ from 'underscore';
import { useContext, useMemo } from 'react';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import type {
  IActivity,
  IInstrumentation,
  IScenario,
} from '@bingads-webui/common-types';

const backupLogger = {
  trace: _.noop,
  error: _.noop,
  signal: _.noop,
};

export type InstrumentLogger = Pick<IActivity, 'trace' | 'error' | 'signal'>;

type Scenario = Pick<IScenario, 'createActivity'> & Record<string, unknown>;

export type Instrumentation = Partial<Pick<IInstrumentation, 'createScenario'>>
  & Record<string, unknown>;

function isFactory<T>(value: unknown): value is { create: () => T } {
  return typeof value === 'object'
    && value !== null
    && 'create' in value
    && typeof value.create === 'function';
}

export const getInstrumentLogger = (
  instrumentation: Instrumentation | null | undefined,
  scenarioName: string,
  activityName: string
): InstrumentLogger => {
  if (!instrumentation || !scenarioName || !activityName) {
    return backupLogger;
  }

  try {
    let scenario;
    const scenarioCandidate = instrumentation[scenarioName];
    if (!scenarioCandidate) {
      scenario = instrumentation.createScenario({
        scenarioName,
        activityName,
      });
    } else if (isFactory<Scenario>(scenarioCandidate)) {
      scenario = scenarioCandidate.create();
    } else {
      return backupLogger;
    }

    let activity;
    const activityCandidate = scenario[activityName];
    if (!activityCandidate) {
      activity = scenario.createActivity(activityName);
    } else if (isFactory<InstrumentLogger>(activityCandidate)) {
      activity = activityCandidate.create();
    } else {
      return backupLogger;
    }

    return activity;
  } catch (e) {
    return backupLogger;
  }
};

export const useInstrumentLogger = ({ scenarioName = '', activityName = '' }: {
  scenarioName?: string;
  activityName?: string;
} = {}) => {
  const instrumentation = useContext(InstrumentationContext);
  const logger = useMemo(
    () => getInstrumentLogger(instrumentation as Instrumentation | undefined, scenarioName, activityName),
    [activityName, instrumentation, scenarioName]
  );

  return logger;
};
